<!--
  DashboardView.vue — 控制台（仪表盘）
  全局数据概览页面，包含：
  1. 收款统计卡片（今日/昨日/近20日均/近20日总）
  2. 蝉豆余额与消耗监控
  3. 近20日整体收款趋势折线图
  4. 每日订单趋势折线图（按渠道维度切换）
  5. 近20日指定应用收款趋势（可切换应用）
  6. 内嵌 LTV 报告表格（可切换应用，支持分页和 CSV 下载）
  7. 手机报订阅与短信下发统计
  8. 视听会员权益领取、H5 跳转生成视频任务、红包立减金与风芒短剧会员领取统计
  9. 商户收款趋势
-->
<template>
  <div>
    <div class="dashboard-toolbar">
      <div
        class="bean-summary"
        :aria-busy="beanLoading"
      >
        <span class="bean-summary-item">蝉豆剩余：{{ beanTotal !== null ? beanTotal.toLocaleString() : '-' }}</span>
        <span class="bean-summary-item">过去7天日均：{{ beanAvg !== null ? beanAvg.toLocaleString() : '-' }}</span>
        <span class="bean-summary-item">总计：{{ beanSum !== null ? beanSum.toLocaleString() : '-' }}</span>
        <span class="bean-summary-item">可用：{{ beanDaysLeft !== null ? beanDaysLeft + ' 天' : '-' }}</span>
      </div>
      <div class="dashboard-refresh-status">
        <div
          class="dashboard-refresh-row"
          role="timer"
          :aria-label="`数据刷新倒计时 ${refreshCountdown} 秒`"
        >
          <span class="dashboard-refresh-label">数据刷新倒计时：</span>
          <span class="refresh-countdown-value">{{ refreshCountdown }}s</span>
        </div>
        <div class="dashboard-refresh-row">
          <span class="dashboard-refresh-label">数据更新时间：</span>
          <time
            class="refresh-time-value"
            :datetime="lastRefreshTimeIso || undefined"
          >{{ lastRefreshTime }}</time>
        </div>
      </div>
      <a-button
        class="dashboard-refresh-button"
        size="large"
        :loading="dashboardRefreshing"
        @click="handleManualRefresh"
      >
        <template #icon>
          <ReloadOutlined />
        </template>
        刷新
      </a-button>
    </div>

    <a-row
      :gutter="[16, 16]"
      type="flex"
      align="stretch"
    >
      <!-- 手机报卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title" title="手机报成功订阅">手机报</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ mobileNewspaperTodayCount !== null ? mobileNewspaperTodayCount.toLocaleString() : '-' }}</strong> 次
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ mobileNewspaperYesterdayCount !== null ? mobileNewspaperYesterdayCount.toLocaleString() : '-' }}</strong> 次
              </span>
            </div>
            <v-chart
              :option="mobileNewspaperTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="手机报成功订阅最近 7 日趋势"
            />
          </a-spin>
        </a-card>
      </a-col>

      <!-- 短信下发卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title">短信下发</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ smsTodayCount !== null ? smsTodayCount.toLocaleString() : '-' }}</strong> 条
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ smsYesterdayCount !== null ? smsYesterdayCount.toLocaleString() : '-' }}</strong> 条
              </span>
            </div>
            <v-chart
              :option="smsTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="短信下发最近 7 日趋势"
            />
          </a-spin>
        </a-card>
      </a-col>

      <!-- 视听会员权益领取卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title" title="视听会员权益领取">视听会员权益领取</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ rightsActivationTodayCount !== null ? rightsActivationTodayCount.toLocaleString() : '-' }}</strong> 次
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ rightsActivationYesterdayCount !== null ? rightsActivationYesterdayCount.toLocaleString() : '-' }}</strong> 次
              </span>
            </div>
            <v-chart
              :option="rightsActivationTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="视听会员权益领取最近 7 日趋势"
            />
          </a-spin>
        </a-card>
      </a-col>

      <!-- H5跳转生成视频任务卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title" title="H5跳转生成视频任务">H5跳转生成视频任务</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ seedanceTaskTodayCount !== null ? seedanceTaskTodayCount.toLocaleString() : '-' }}</strong> 个
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ seedanceTaskYesterdayCount !== null ? seedanceTaskYesterdayCount.toLocaleString() : '-' }}</strong> 个
              </span>
            </div>
            <v-chart
              :option="seedanceTaskTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="H5 跳转生成视频任务最近 7 日趋势"
            />
          </a-spin>
        </a-card>
      </a-col>

      <!-- 红包立减金领取卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card lijianjin-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title" title="红包立减金领取">红包立减金领取</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ liJianJinTodayCount !== null ? liJianJinTodayCount.toLocaleString() : '-' }}</strong> 次
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ liJianJinYesterdayCount !== null ? liJianJinYesterdayCount.toLocaleString() : '-' }}</strong> 次
              </span>
            </div>
            <v-chart
              :option="liJianJinTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="红包立减金领取最近 7 日趋势"
            />
            <div class="task-record-amount-summary">
              <span>今日金额 {{ formatYuanAmount(liJianJinTodayAmount) }}</span>
              <span>昨日金额 {{ formatYuanAmount(liJianJinYesterdayAmount) }}</span>
            </div>
          </a-spin>
        </a-card>
      </a-col>

      <!-- 风芒短剧会员领取卡片 -->
      <a-col
        :xs="24"
        :sm="24"
        :lg="4"
      >
        <a-card
          :bordered="false"
          class="stat-card bean-card task-record-card"
        >
          <a-spin :spinning="taskRecordStatLoading">
            <div class="bean-header">
              <span class="bean-title" title="风芒短剧会员领取">风芒短剧会员领取</span>
            </div>
            <div class="task-record-summary">
              <span class="task-record-summary-item">
                今日：<strong class="task-record-summary-value">{{ fmShortVideoTodayCount !== null ? fmShortVideoTodayCount.toLocaleString() : '-' }}</strong> 次
              </span>
              <span class="task-record-summary-item">
                昨日：<strong class="task-record-summary-value">{{ fmShortVideoYesterdayCount !== null ? fmShortVideoYesterdayCount.toLocaleString() : '-' }}</strong> 次
              </span>
            </div>
            <v-chart
              :option="fmShortVideoTrendOption"
              autoresize
              class="task-record-trend-chart"
              aria-label="风芒短剧会员领取最近 7 日趋势"
            />
          </a-spin>
        </a-card>
      </a-col>
    </a-row>

    <a-row
      :gutter="[16, 16]"
      type="flex"
      align="stretch"
      style="margin-top: 16px"
    >
      <a-col
        :xs="24"
        :lg="12"
      >
        <a-card
          title="40日整体收款趋势"
          :bordered="false"
        >
          <a-spin :spinning="orderChartLoading">
            <v-chart
              :option="orderChartOption"
              autoresize
              group="dashboard-link"
              style="height: 360px"
            />
            <div
              class="chart-today-bar"
              v-if="orderChartPay.length"
            >
              <span>今日收款：<b style="color:#1677ff">{{ formatAmt(orderChartPay[orderChartPay.length - 1] || 0) }}</b></span>
              <span style="margin-left:12px">今日退款：<b style="color:#ff4d4f">{{ formatAmt(orderChartRefund[orderChartRefund.length - 1] || 0) }}</b></span>
              <span style="margin-left:12px">收-退：<b style="color:#52c41a">{{ formatAmt((orderChartPay[orderChartPay.length - 1] || 0) - (orderChartRefund[orderChartRefund.length - 1] || 0)) }}</b></span>
            </div>
            <!-- 收款统计表格 -->
            <table
              class="stat-table"
              style="margin-top: 12px"
            >
              <thead>
                <tr>
                  <th class="st-th st-label-col"></th>
                  <th class="st-th">收款</th>
                  <th class="st-th">退款</th>
                  <th class="st-th">收-退</th>
                  <th class="st-th">首购</th>
                  <th class="st-th">续订</th>
                </tr>
              </thead>
              <tbody>
                <tr class="st-row">
                  <td class="st-td st-label-col">今日</td>
                  <td
                    class="st-td"
                    style="color:#1677ff"
                  >{{ todayPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#ff4d4f"
                  >{{ todayRefundFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#52c41a"
                  >{{ todayNetFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#722ed1"
                  >{{ todayFirstPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#fa8c16"
                  >{{ todayRenewFmt }}</td>
                </tr>
                <tr class="st-row st-row-alt">
                  <td class="st-td st-label-col">昨日</td>
                  <td
                    class="st-td"
                    style="color:#1677ff"
                  >{{ yestPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#ff4d4f"
                  >{{ yestRefundFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#52c41a"
                  >{{ yestNetFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#722ed1"
                  >{{ yestFirstPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#fa8c16"
                  >{{ yestRenewFmt }}</td>
                </tr>
                <tr class="st-row">
                  <td class="st-td st-label-col">近40日均</td>
                  <td
                    class="st-td"
                    style="color:#1677ff"
                  >{{ avgPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#ff4d4f"
                  >{{ avgRefundFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#52c41a"
                  >{{ avgNetFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#722ed1"
                  >{{ avgFirstPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#fa8c16"
                  >{{ avgRenewFmt }}</td>
                </tr>
                <tr class="st-row st-row-alt">
                  <td class="st-td st-label-col">近40日总</td>
                  <td
                    class="st-td"
                    style="color:#1677ff"
                  >{{ totalPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#ff4d4f"
                  >{{ totalRefundFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#52c41a"
                  >{{ totalNetFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#722ed1"
                  >{{ totalFirstPayFmt }}</td>
                  <td
                    class="st-td"
                    style="color:#fa8c16"
                  >{{ totalRenewFmt }}</td>
                </tr>
              </tbody>
            </table>
          </a-spin>
        </a-card>
      </a-col>
      <a-col
        :xs="24"
        :lg="12"
      >
        <a-card :bordered="false">
          <template #title>
            预扣款与订单统计趋势
            <a-radio-group
              v-model:value="orderStatDimension"
              size="small"
              style="margin-left: 12px"
            >
              <a-radio-button value="All">全部</a-radio-button>
              <a-radio-button value="Total">总数</a-radio-button>
              <a-radio-button value="Kq">快钱</a-radio-button>
              <a-radio-button value="Hlb">合利宝</a-radio-button>
              <a-radio-button value="Jd">京东</a-radio-button>
            </a-radio-group>
          </template>
          <a-spin :spinning="orderStatLoading">
            <v-chart
              :option="orderStatChartOption"
              :update-options="{ notMerge: true }"
              autoresize
              group="dashboard-link"
              style="height: 360px"
              @legendselectchanged="onLegendSelectChanged"
            />
            <!-- "全部"模式：自定义指标图例 + 渠道线型说明 -->
            <template v-if="orderStatDimension === 'All'">
              <div class="custom-legend">
                <span
                  v-for="m in orderStatMetrics"
                  :key="m.label"
                  class="custom-legend-item"
                  :class="{ 'is-off': !orderStatMetricVisible[m.label] }"
                  @click="toggleMetric(m.label)"
                >
                  <span
                    class="custom-legend-dot"
                    :style="{ background: orderStatMetricVisible[m.label] ? m.color : '#ccc' }"
                  ></span>
                  {{ m.label }}
                </span>
              </div>
              <div class="channel-hint">
                <span class="channel-hint-item"><span class="ch-line ch-solid"></span>总数</span>
                <span class="channel-hint-item"><span class="ch-line ch-dash"></span>快钱</span>
                <span class="channel-hint-item"><span class="ch-line ch-dot"></span>合利宝</span>
                <span class="channel-hint-item"><span class="ch-line ch-dashdot"></span>京东</span>
              </div>
            </template>
            <!-- 要扣款订单数 颜色标注 -->
            <div
              class="deduct-stat-bar"
              v-if="orderStatToday"
            >
              <span class="deduct-stat-title">今日预计扣款</span>
              <span
                class="deduct-tag"
                style="background:#fa8c16"
              >
                总 {{ orderStatToday.toDeductTotal?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#1677ff"
              >
                快钱 {{ orderStatToday.toDeductKq?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#52c41a"
              >
                合利宝 {{ orderStatToday.toDeductHlb?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#722ed1"
              >
                京东 {{ orderStatToday.toDeductJd?.toLocaleString() ?? '-' }}
              </span>
            </div>
            <div
              class="deduct-stat-bar"
              v-if="orderStatToday"
            >
              <span class="deduct-stat-title">新签约</span>
              <span
                class="deduct-tag"
                style="background:#13c2c2"
              >
                总 {{ orderStatToday.retainedTotal?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#1677ff"
              >
                快钱 {{ orderStatToday.retainedKq?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#52c41a"
              >
                合利宝 {{ orderStatToday.retainedHlb?.toLocaleString() ?? '-' }}
              </span>
              <span
                class="deduct-tag"
                style="background:#722ed1"
              >
                京东 {{ orderStatToday.retainedJd?.toLocaleString() ?? '-' }}
              </span>
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>

    <a-row
      :gutter="[16, 16]"
      type="flex"
      align="stretch"
      style="margin-top: 16px"
    >
      <a-col :xs="24">
        <a-card
          :bordered="false"
        >
          <template #title>
            <div class="merchant-card-title">
              <span class="merchant-card-title-text">商户收款趋势</span>
              <a-radio-group
                class="merchant-day-switch"
                v-model:value="merchantChartDays"
                size="small"
                button-style="solid"
              >
                <a-radio-button
                  v-for="day in MERCHANT_CHART_DAY_OPTIONS"
                  :key="day"
                  :value="day"
                >
                  {{ day }}日
                </a-radio-button>
              </a-radio-group>
            </div>
          </template>
          <a-spin :spinning="merchantChartLoading">
            <template v-if="merchantChartHasData">
              <v-chart
                :option="merchantChartOption"
                :update-options="{ notMerge: true }"
                autoresize
                style="height: 360px"
                @legendselectchanged="onMerchantMetricLegendSelectChanged"
              />
              <div class="merchant-list-row">
                <span class="merchant-list-label">公司主体：</span>
                <button
                  v-for="merchant in merchantList"
                  :key="merchant.id"
                  type="button"
                  class="merchant-tag"
                  :class="{ 'is-off': !isMerchantVisible(merchant.id) }"
                  :aria-pressed="isMerchantVisible(merchant.id)"
                  :title="merchant.displayName"
                  @click="toggleMerchant(merchant.id)"
                >
                  <span
                    class="merchant-tag-line"
                    :style="merchantTagLineStyle(merchant.id)"
                  ></span>
                  {{ merchant.displayName }}
                  <span
                    class="merchant-tag-dot"
                    :style="merchantTagDotStyle(merchant.id)"
                  ></span>
                </button>
                <span
                  v-if="!merchantList.length"
                  class="merchant-list-empty"
                >暂无公司主体</span>
              </div>
              <div class="merchant-pay-summary">
                <div class="merchant-pay-summary-title">
                  公司主体收款概览
                  <span>金额：万元；订单：单</span>
                </div>
                <div class="merchant-pay-summary-scroll">
                  <table class="merchant-pay-summary-table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          class="merchant-pay-summary-label"
                        >日期</th>
                        <th
                          v-for="company in merchantPaySummaryCompanies"
                          :key="company.id"
                          scope="col"
                        >
                          {{ company.label }}
                        </th>
                        <th
                          scope="col"
                          class="merchant-pay-summary-total"
                        >合计</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in merchantPaySummaryRows"
                        :key="row.key"
                      >
                        <th
                          scope="row"
                          class="merchant-pay-summary-label"
                        >{{ row.label }}</th>
                        <td
                          v-for="company in merchantPaySummaryCompanies"
                          :key="company.id"
                          class="merchant-pay-summary-cell"
                        >
                          <div class="merchant-pay-summary-amount">
                            {{ row.cells[company.id].amount }}
                          </div>
                          <div class="merchant-pay-summary-counts">
                            <span>首购 {{ row.cells[company.id].firstPayOrderCount }}</span>
                            <span
                              class="merchant-pay-summary-separator"
                            >/</span>
                            <span>续订 {{ row.cells[company.id].renewOrderCount }}</span>
                            <span
                              class="merchant-pay-summary-separator"
                            >/</span>
                            <span>成功回传 {{ row.cells[company.id].payReportOrderCount }}</span>
                          </div>
                        </td>
                        <td class="merchant-pay-summary-cell merchant-pay-summary-total">
                          <div class="merchant-pay-summary-amount">
                            {{ row.total.amount }}
                          </div>
                          <div class="merchant-pay-summary-counts">
                            <span>首购 {{ row.total.firstPayOrderCount }}</span>
                            <span
                              class="merchant-pay-summary-separator"
                            >/</span>
                            <span>续订 {{ row.total.renewOrderCount }}</span>
                            <span
                              class="merchant-pay-summary-separator"
                            >/</span>
                            <span>成功回传 {{ row.total.payReportOrderCount }}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
            <a-empty
              v-else
              description="暂无商户收款趋势数据"
            />
          </a-spin>
        </a-card>
      </a-col>
    </a-row>

    <!-- 近20日应用收款趋势（已隐藏，后续有需求再放开） -->
    <!-- <a-row
      :gutter="[16, 16]"
      type="flex"
      align="stretch"
      style="margin-top: 16px"
    >
      <a-col
        :xs="24"
        :lg="24"
      >
        <a-card :bordered="false">
          <template #title>
            20日应用收款趋势
            <a-select
              v-model:value="selectedAppId"
              size="small"
              style="width: 150px; margin-left: 12px"
            >
              <a-select-option :value="94">权益卡-ox (94)</a-select-option>
              <a-select-option :value="259">权益卡-gsht (259)</a-select-option>
            </a-select>
          </template>
          <a-spin :spinning="appChartLoading">
            <v-chart
              :option="appChartOption"
              autoresize
              style="height: 260px"
            />
          </a-spin>
        </a-card>
      </a-col>
    </a-row> -->

    <!-- LTV 留存率热力表 -->
    <a-row
      :gutter="[16, 16]"
      type="flex"
      align="stretch"
      style="margin-top: 16px"
    >
      <a-col :xs="24">
        <a-card
          :bordered="false"
          class="retention-card"
        >
          <template #title>
            纬度留存分析
            <a-select
              v-model:value="retentionDimensionId"
              size="small"
              style="width: 260px; margin-left: 12px"
              placeholder="选择留存维度"
              :options="retentionDimOptions"
              @change="onRetentionDimChange"
            />
            <a-radio-group
              v-model:value="retentionGroupBy"
              size="small"
              button-style="solid"
              style="margin-left: 8px"
            >
              <a-radio-button value="day">日</a-radio-button>
              <a-radio-button value="week">周</a-radio-button>
              <a-radio-button value="month">月</a-radio-button>
            </a-radio-group>
            <span class="retention-center-title">+n 留存率</span>
          </template>
          <template #extra>
            <a-popover
              placement="bottomRight"
              trigger="hover"
              overlay-class-name="retention-help-popover"
            >
              <template #title>
                <span style="font-weight: 600; font-size: 15px">数据指标说明</span>
              </template>
              <template #content>
                <div class="rh-items">
                  <div class="rh-item">
                    <span class="rh-label">Mn内容:</span>
                    第一行:留存率, 第二行:订单数。
                  </div>
                  <div class="rh-item">
                    <span class="rh-label">M0订单:</span>
                    所选日期内的首次付费。
                  </div>
                  <div class="rh-item">
                    <span class="rh-label">M1留存率:</span>
                    所选日期的M1 59.9订单数/M0订单数。
                  </div>

                  <div class="rh-item">
                    <span class="rh-label">M1后的留存率:</span>
                    所选日期的M1后的订单数/M1订单数。
                  </div>
                  <div class="rh-item">
                    <span class="rh-label">N周留存率:</span>
                    某自然周新增订单，在该周后第N个自然周付费订单。该周后第N个自然周使用过应用的订单/该周新增订单。
                  </div>
                  <div class="rh-item">
                    <span class="rh-label">N月留存率:</span>
                    某自然月新增订单，在该月后第N个自然月付费订单。该自然月后第N个自然月中使用过应用的订单/该自然月新增订单。
                  </div>
                </div>
              </template>
              <question-circle-outlined style="margin-left: 8px; color: #999; cursor: pointer" />
            </a-popover>
          </template>
          <a-spin :spinning="retentionLoading">
            <div class="retention-table-wrap">
              <table class="retention-table bank-retention-table">
                <thead>
                  <tr>
                    <th class="rt-th rt-fixed">日期</th>
                    <th class="rt-th">M0订单</th>
                    <th
                      class="rt-th"
                      v-for="n in retentionMaxM"
                      :key="n"
                    >M{{ n }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in retentionRows"
                    :key="idx"
                  >
                    <td class="rt-td rt-fixed">{{ row.label }}</td>
                    <td class="rt-td">{{ row.m0Count }}</td>
                    <td
                      class="rt-td"
                      v-for="n in retentionMaxM"
                      :key="n"
                      :style="retentionCellStyle(row.rates[n])"
                    >
                      <template v-if="row.rates[n] != null && row.rates[n] !== 0">
                        <div class="rt-rate">{{ row.rates[n] }}%</div>
                        <div class="rt-sub">{{ row.counts[n] ?? 0 }}</div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>

    <!-- 渠道留存分析（支付渠道 Cohort） -->
    <a-row
      :gutter="[16, 16]"
      style="margin-top: 16px"
    >
      <a-col :span="24">
        <a-card
          :bordered="false"
          class="retention-card"
        >
          <template #title>
            渠道留存分析
            <a-select
              v-model:value="chPayType"
              size="small"
              style="width: 100px; margin-left: 12px"
              @change="fetchChData"
            >
              <a-select-option value="kq">快钱</a-select-option>
              <a-select-option value="hlb">合利宝</a-select-option>
              <a-select-option value="jd">京东</a-select-option>
            </a-select>
            <a-radio-group
              v-model:value="chGroupBy"
              size="small"
              button-style="solid"
              style="margin-left: 8px"
            >
              <a-radio-button value="day">日</a-radio-button>
              <a-radio-button value="week">周</a-radio-button>
              <a-radio-button value="month">月</a-radio-button>
            </a-radio-group>
            <span class="retention-center-title">+n 留存率</span>
          </template>
          <a-spin :spinning="chLoading">
            <div class="retention-table-wrap">
              <table class="retention-table">
                <thead>
                  <tr>
                    <th class="rt-th rt-fixed">日期</th>
                    <th class="rt-th">M0订单</th>
                    <th
                      class="rt-th"
                      v-for="n in chMaxM"
                      :key="n"
                    >M{{ n }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in chRows"
                    :key="idx"
                  >
                    <td class="rt-td rt-fixed">{{ row.label }}</td>
                    <td class="rt-td">{{ row.m0Count }}</td>
                    <td
                      class="rt-td"
                      v-for="n in chMaxM"
                      :key="n"
                      :style="retentionCellStyle(row.rates[n])"
                    >
                      <template v-if="row.rates[n] != null && row.rates[n] !== 0">
                        <div class="rt-rate">{{ row.rates[n] }}%</div>
                        <div class="rt-sub">{{ row.counts[n] ?? 0 }}</div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>

    <!-- 银行留存分析（支付渠道+银行 Cohort） -->
    <a-row
      :gutter="[16, 16]"
      style="margin-top: 16px"
    >
      <a-col :span="24">
        <a-card
          :bordered="false"
          class="retention-card"
        >
          <template #title>
            银行留存分析
            <a-select
              v-model:value="bkBankCode"
              size="small"
              style="width: 150px; margin-left: 12px"
              placeholder="银行"
              allowClear
              @change="fetchBkData"
              :options="bkBankOptions"
            />
            <a-select
              v-model:value="bkPayType"
              size="small"
              style="width: 100px; margin-left: 12px"
              @change="fetchBkData"
            >
              <a-select-option value="">全部渠道</a-select-option>
              <a-select-option value="kq">快钱</a-select-option>
              <a-select-option value="hlb">合利宝</a-select-option>
              <a-select-option value="jd">京东</a-select-option>
            </a-select>
            <a-radio-group
              v-model:value="bkGroupBy"
              size="small"
              button-style="solid"
              style="margin-left: 8px"
            >
              <a-radio-button value="day">日</a-radio-button>
              <a-radio-button value="week">周</a-radio-button>
              <a-radio-button value="month">月</a-radio-button>
            </a-radio-group>
            <span class="retention-center-title">+n 留存率</span>
          </template>
          <a-spin :spinning="bkLoading">
            <div class="retention-table-wrap">
              <table class="retention-table">
                <thead>
                  <tr>
                    <th class="rt-th rt-fixed">日期</th>
                    <th class="rt-th">渠道</th>
                    <th class="rt-th">M0订单</th>
                    <th
                      class="rt-th"
                      v-for="n in bkMaxM"
                      :key="n"
                    >M{{ n }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in bkDisplayRows"
                    :key="`${row.dateLabel}_${row.payType}_${idx}`"
                    :class="getBkRowClass(row.payType)"
                  >
                    <td
                      v-if="row.showDate"
                      class="rt-td rt-fixed bk-date-cell"
                      :rowspan="row.rowSpan"
                    >{{ row.dateLabel }}</td>
                    <td
                      class="rt-td bk-meta-cell bk-channel-cell"
                      :style="getBkChannelCellStyle(row.payType)"
                    >{{ row.payTypeLabel }}</td>
                    <td
                      class="rt-td bk-meta-cell"
                      :style="getBkRowBgStyle(row.payType, true)"
                    >{{ row.m0Count }}</td>
                    <td
                      class="rt-td"
                      v-for="n in bkMaxM"
                      :key="n"
                      :style="getBkCellStyle(row, n)"
                    >
                      <template v-if="row.rates[n] != null && row.rates[n] !== 0">
                        <div class="rt-rate">{{ row.rates[n] }}%</div>
                        <div class="rt-sub">{{ row.counts[n] ?? 0 }}</div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

import { message } from "ant-design-vue";
import {
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons-vue";
import VChart from "vue-echarts";

/* ===== ECharts 按需引入（减小打包体积） ===== */
import { use, connect } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart } from "echarts/charts";
import {
  AriaComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
} from "echarts/components";
import { apiUrl, analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";
import { getAllowedDimensionIds } from "@/utils/permission";

/* 注册 ECharts 组件 */
use([
  CanvasRenderer,
  LineChart,
  BarChart,
  AriaComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
]);

// ========================
// 每日订单（替代原饼图区域）
// ========================

const orderStatLoading = ref(false);
const orderStatData = ref([]);
const orderStatDimension = ref(localStorage.getItem("os_dim") || "Total");

const orderStatMetrics = [
  { key: "newSign", label: "新签约", color: "#1677ff" },
  { key: "toDeduct", label: "预计扣款", color: "#fa8c16" },
  { key: "deducted", label: "全部扣款", color: "#52c41a" },
  { key: "deductedM0", label: "首次扣款", color: "#722ed1" },
  { key: "deductedM1", label: "非首次扣款", color: "#ff4d4f" },
  { key: "retained", label: "留存签约", color: "#13c2c2" },
];

// "全部"模式下各指标是否可见
const defaultMetricVisible = {
  新签约: true,
  预计扣款: true,
  全部扣款: true,
  首次扣款: true,
  非首次扣款: true,
  留存签约: false,
};
const savedVisible = (() => {
  try {
    return JSON.parse(localStorage.getItem("os_vis"));
  } catch {
    return null;
  }
})();
const orderStatMetricVisible = ref(savedVisible || { ...defaultMetricVisible });
const toggleMetric = (label) => {
  orderStatMetricVisible.value[label] = !orderStatMetricVisible.value[label];
  localStorage.setItem("os_vis", JSON.stringify(orderStatMetricVisible.value));
};

// 非全部模式下 ECharts 内置图例的 selected 状态
const defaultLegendSelected = { 留存签约: false };
const savedLegendSelected = (() => {
  try {
    return JSON.parse(localStorage.getItem("os_leg"));
  } catch {
    return null;
  }
})();
const orderStatLegendSelected = ref(
  savedLegendSelected || { ...defaultLegendSelected }
);
const onLegendSelectChanged = (e) => {
  orderStatLegendSelected.value = { ...e.selected };
  localStorage.setItem("os_leg", JSON.stringify(e.selected));
};

watch(orderStatDimension, (v) => localStorage.setItem("os_dim", v));

const orderStatToday = computed(() => {
  if (!orderStatData.value.length) return null;
  return orderStatData.value[0];
});

const orderStatChartOption = computed(() => {
  const dim = orderStatDimension.value;
  const rows = [...orderStatData.value].reverse();
  const dates = rows.map((r) => r.statDate.slice(5));

  const metrics = orderStatMetrics;
  const channels = [
    { suffix: "Total", label: "总数", dash: "solid", shade: 0, width: 2.5 },
    { suffix: "Kq", label: "快钱", dash: [5, 5], shade: 0.3, width: 2 },
    { suffix: "Hlb", label: "合利宝", dash: [2, 3], shade: 0.5, width: 1.5 },
    { suffix: "Jd", label: "京东", dash: [8, 4, 2, 4], shade: 0.65, width: 1 },
  ];

  // 颜色加白（变浅）
  const lighten = (hex, amt) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r + (255 - r) * amt)},${Math.round(
      g + (255 - g) * amt
    )},${Math.round(b + (255 - b) * amt)})`;
  };

  let series, legendCfg;
  if (dim === "All") {
    series = [];
    const vis = orderStatMetricVisible.value;
    // 只生成可见指标的series，不可见的完全不加入，保证axisPointer/connect联动正常
    for (const m of metrics) {
      if (!vis[m.label]) continue;
      for (const ch of channels) {
        const name = m.label + "-" + ch.label;
        series.push({
          name,
          type: "line",
          smooth: true,
          data: rows.map((r) => r[m.key + ch.suffix]),
          itemStyle: { color: lighten(m.color, ch.shade) },
          lineStyle: { type: ch.dash, width: ch.width },
        });
      }
    }
    legendCfg = { show: false };
  } else {
    const legendData = metrics.map((m) => m.label);
    legendCfg = {
      show: true,
      data: legendData,
      selected: { ...orderStatLegendSelected.value },
      bottom: 0,
    };
    series = metrics.map((m) => ({
      name: m.label,
      type: "line",
      smooth: true,
      data: rows.map((r) => r[m.key + dim]),
      itemStyle: { color: m.color },
    }));
  }

  const tooltipCfg = { trigger: "axis", axisPointer: { type: "line" } };
  if (dim === "All") {
    tooltipCfg.formatter = (params) => {
      const shown = params.filter((p) => p.value != null);
      if (!shown.length) return params[0]?.axisValue || "";
      let s = shown[0].axisValue;
      for (const p of shown)
        s += `<br/>${p.marker}${p.seriesName}：${p.value?.toLocaleString()}`;
      return s;
    };
  }

  return {
    tooltip: tooltipCfg,
    legend: legendCfg,
    grid: {
      top: 10,
      left: 0,
      right: 8,
      bottom: dim === "All" ? 10 : 36,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: dates,
      axisLabel: { fontSize: 11, rotate: 30 },
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 11 },
    },
    series,
  };
});

const fetchOrderStat = async () => {
  orderStatLoading.value = true;
  try {
    const [json1, json2] = await Promise.all([
      get(analyticsUrl("/order/stats/daily/order-stat"), {
        pageIdx: 1,
        pageSize: 20,
        startDate: "",
        endDate: "",
      }),
      get(analyticsUrl("/order/stats/daily/order-stat"), {
        pageIdx: 2,
        pageSize: 20,
        startDate: "",
        endDate: "",
      }),
    ]);
    let list = [];
    if (json1.code === 200 && json1.data?.data)
      list = list.concat(json1.data.data);
    if (json2.code === 200 && json2.data?.data)
      list = list.concat(json2.data.data);
    orderStatData.value = list;
  } catch (e) {
    message.error("每日订单数据获取失败");
  } finally {
    orderStatLoading.value = false;
  }
};

// ========================
// 蝉豆相关状态 & 接口
// ========================

/** 蝉豆剩余总数 */
const beanTotal = ref(null);
/** 过去7天日均消耗 */
const beanAvg = ref(null);
/** 过去7天总计消耗 */
const beanSum = ref(null);
/** 蝉豆数据加载状态 */
const beanLoading = ref(false);

/** 计算蝉豆可用天数：剩余数量 ÷ 日均消耗 */
const beanDaysLeft = computed(() => {
  if (beanTotal.value !== null && beanAvg.value !== null && beanAvg.value > 0) {
    return Math.floor(beanTotal.value / beanAvg.value);
  }
  return null;
});

/**
 * 获取蝉豆余额
 * 接口：GET /v1/chatgpt/cj/company/bean
 */
const fetchBeanBalance = async () => {
  try {
    const json = await get(apiUrl("/v1/chatgpt/cj/company/bean"));
    if (json.code === 200) {
      beanTotal.value = json.data?.resi_total_bean ?? null;
    } else {
      message.warning("蝉豆余额查询失败：" + (json.content || "未知错误"));
    }
  } catch (e) {
    message.error("蝉豆余额接口请求失败");
  }
};

/**
 * 获取蝉豆过去7天消耗数据
 * 接口：GET /v1/chatgpt/cj/days7/bean/consume
 */
const fetchBeanConsume = async () => {
  try {
    const json = await get(apiUrl("/v1/chatgpt/cj/days7/bean/consume"));
    if (json.code === 200) {
      beanAvg.value = json.data?.avg_beam_amount ?? null;
      beanSum.value = json.data?.sum_beam_amount ?? null;
    } else {
      message.warning("蝉豆消耗查询失败：" + (json.content || "未知错误"));
    }
  } catch (e) {
    message.error("蝉豆消耗接口请求失败");
  }
};

// ========================
// 手机报订阅与短信下发统计
// ========================

const getCurrentDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getOffsetDateString = (dayOffset) => {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  return getCurrentDateString(date);
};

const mobileNewspaperTodayCount = ref(null);
const mobileNewspaperYesterdayCount = ref(null);
const mobileNewspaperRecent7Days = ref([]);
const smsTodayCount = ref(null);
const smsYesterdayCount = ref(null);
const smsRecent7Days = ref([]);

// ========================
// 权益激活、Seedance 任务、立减金与风芒短剧会员领取统计
// ========================

const rightsActivationTodayCount = ref(null);
const rightsActivationYesterdayCount = ref(null);
const seedanceTaskTodayCount = ref(null);
const seedanceTaskYesterdayCount = ref(null);
const liJianJinTodayCount = ref(null);
const liJianJinTodayAmount = ref(null);
const liJianJinYesterdayCount = ref(null);
const liJianJinYesterdayAmount = ref(null);
const fmShortVideoTodayCount = ref(null);
const fmShortVideoYesterdayCount = ref(null);
const rightsActivationRecent7Days = ref([]);
const seedanceTaskRecent7Days = ref([]);
const liJianJinRecent7Days = ref([]);
const fmShortVideoRecent7Days = ref([]);
const taskRecordStatLoading = ref(false);

const normalizeRecent7Days = (dailyStats) => {
  if (!Array.isArray(dailyStats)) return [];
  return dailyStats
    .map((item) => ({
      date: String(item?.date || ""),
      count: Number(item?.count || 0),
    }))
    .filter((item) => item.date);
};

const buildTaskRecordTrendOption = (dailyStats, label) => {
  const dates = dailyStats.map((item) => item.date);
  const counts = dailyStats.map((item) => item.count);
  return {
    animation: false,
    aria: {
      enabled: true,
      description: `${label}最近 7 日数量依次为 ${counts.join("、") || "暂无数据"}`,
    },
    grid: {
      top: 10,
      right: 6,
      bottom: 22,
      left: 6,
    },
    tooltip: {
      trigger: "axis",
      confine: true,
      formatter: (params) => {
        const point = Array.isArray(params) ? params[0] : params;
        if (!point) return "";
        return `${point.axisValue}<br/>数量：${Number(
          point.data || 0
        ).toLocaleString()}`;
      },
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: dates,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "#d9d9d9" } },
      axisLabel: {
        color: "rgba(0, 0, 0, 0.45)",
        fontSize: 10,
        margin: 6,
        formatter: (value, index) =>
          index === 0 || index === dates.length - 1 ? value.slice(5) : "",
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      minInterval: 1,
      splitNumber: 2,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: {
        lineStyle: {
          color: "rgba(0, 0, 0, 0.06)",
          type: "dashed",
        },
      },
    },
    series: [
      {
        name: label,
        type: "line",
        data: counts,
        smooth: 0.3,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2, color: "#1677ff" },
        itemStyle: { color: "#1677ff" },
        areaStyle: { color: "rgba(22, 119, 255, 0.10)" },
      },
    ],
  };
};

const mobileNewspaperTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    mobileNewspaperRecent7Days.value,
    "手机报成功订阅"
  )
);
const smsTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    smsRecent7Days.value,
    "短信下发"
  )
);
const rightsActivationTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    rightsActivationRecent7Days.value,
    "视听会员权益领取"
  )
);
const seedanceTaskTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    seedanceTaskRecent7Days.value,
    "H5跳转生成视频任务"
  )
);
const liJianJinTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    liJianJinRecent7Days.value,
    "红包立减金领取"
  )
);
const fmShortVideoTrendOption = computed(() =>
  buildTaskRecordTrendOption(
    fmShortVideoRecent7Days.value,
    "风芒短剧会员领取"
  )
);

const formatYuanAmount = (yuan) => {
  if (yuan === null || !Number.isFinite(Number(yuan))) return "-";
  return Number(yuan).toLocaleString("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fetchTaskRecordStat = async () => {
  taskRecordStatLoading.value = true;
  try {
    const json = await get(
      analyticsUrl("/dashboard/task-record-stats")
    );
    if (json.code === 200 && json.data) {
      const mobileNewspaperSubscription = json.data.mobileNewspaperSubscription || {};
      const smsSend = json.data.smsSend || {};
      const rightsActivation = json.data.rightsActivation || {};
      const seedanceTaskSubmit = json.data.seedanceTaskSubmit || {};
      const liJianJinActivation =
        json.data.wechatPayLiJianJinActivation || {};
      const fmShortVideoActivation =
        json.data.fmShortVideoActivation || {};
      mobileNewspaperTodayCount.value = Number(
        mobileNewspaperSubscription.todayCount || 0
      );
      mobileNewspaperYesterdayCount.value = Number(
        mobileNewspaperSubscription.yesterdayCount || 0
      );
      mobileNewspaperRecent7Days.value = normalizeRecent7Days(
        mobileNewspaperSubscription.recent7Days
      );
      smsTodayCount.value = Number(
        smsSend.todayCount || 0
      );
      smsYesterdayCount.value = Number(
        smsSend.yesterdayCount || 0
      );
      smsRecent7Days.value = normalizeRecent7Days(smsSend.recent7Days);
      rightsActivationTodayCount.value = Number(
        rightsActivation.todayCount || 0
      );
      rightsActivationYesterdayCount.value = Number(
        rightsActivation.yesterdayCount || 0
      );
      seedanceTaskTodayCount.value = Number(
        seedanceTaskSubmit.todayCount || 0
      );
      seedanceTaskYesterdayCount.value = Number(
        seedanceTaskSubmit.yesterdayCount || 0
      );
      liJianJinTodayCount.value = Number(
        liJianJinActivation.todayCount || 0
      );
      liJianJinTodayAmount.value = Number(
        liJianJinActivation.todayAmount || 0
      );
      liJianJinYesterdayCount.value = Number(
        liJianJinActivation.yesterdayCount || 0
      );
      liJianJinYesterdayAmount.value = Number(
        liJianJinActivation.yesterdayAmount || 0
      );
      fmShortVideoTodayCount.value = Number(
        fmShortVideoActivation.todayCount || 0
      );
      fmShortVideoYesterdayCount.value = Number(
        fmShortVideoActivation.yesterdayCount || 0
      );
      rightsActivationRecent7Days.value = normalizeRecent7Days(
        rightsActivation.recent7Days
      );
      seedanceTaskRecent7Days.value = normalizeRecent7Days(
        seedanceTaskSubmit.recent7Days
      );
      liJianJinRecent7Days.value = normalizeRecent7Days(
        liJianJinActivation.recent7Days
      );
      fmShortVideoRecent7Days.value = normalizeRecent7Days(
        fmShortVideoActivation.recent7Days
      );
    } else {
      message.warning(
        "控制台卡片统计查询失败：" + (json.content || "未知错误")
      );
    }
  } catch (e) {
    message.error("控制台卡片统计接口请求失败");
  } finally {
    taskRecordStatLoading.value = false;
  }
};

// ========================
// 收款统计卡片（今日/昨日/近20日）
// ========================

/** 今日收款金额（分） */
const todayPayAmount = ref(0);
/** 今日退款金额（分） */
const todayRefundAmount = ref(0);
/** 今日首购金额（分） */
const todayFirstPayAmount = ref(0);
/** 今日续订金额（分） */
const todayRenewAmount = ref(0);
/** 昨日收款金额（分） */
const yestPayAmount = ref(0);
/** 昨日退款金额（分） */
const yestRefundAmount = ref(0);
/** 昨日首购金额（分） */
const yestFirstPayAmount = ref(0);
/** 昨日续订金额（分） */
const yestRenewAmount = ref(0);

/**
 * 金额格式化：分 → 万元，带 "w" 后缀
 * 用于概览卡片的紧凑展示
 * @param {number} fen - 金额（单位：分）
 * @returns {string} 如 "¥2.88w"
 */
const formatAmt = (fen) =>
  "¥" +
  (fen / 100 / 10000).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) +
  "w";

/* 今日格式化计算属性 */
const todayPayFmt = computed(() => formatAmt(todayPayAmount.value));
const todayRefundFmt = computed(() => formatAmt(todayRefundAmount.value));
const todayNetFmt = computed(() =>
  formatAmt(todayPayAmount.value - todayRefundAmount.value)
);
const todayFirstPayFmt = computed(() => formatAmt(todayFirstPayAmount.value));
const todayRenewFmt = computed(() => formatAmt(todayRenewAmount.value));

/* 昨日格式化计算属性 */
const yestPayFmt = computed(() => formatAmt(yestPayAmount.value));
const yestRefundFmt = computed(() => formatAmt(yestRefundAmount.value));
const yestNetFmt = computed(() =>
  formatAmt(yestPayAmount.value - yestRefundAmount.value)
);
const yestFirstPayFmt = computed(() => formatAmt(yestFirstPayAmount.value));
const yestRenewFmt = computed(() => formatAmt(yestRenewAmount.value));

/** 近20日首购金额数组（分） */
const orderChartFirstPay = ref([]);
/** 近20日续订金额数组（分） */
const orderChartRenew = ref([]);

/* 近20日汇总格式化计算属性 */
const totalPayFmt = computed(() =>
  formatAmt(orderChartPay.value.reduce((a, b) => a + b, 0))
);
const totalRefundFmt = computed(() =>
  formatAmt(orderChartRefund.value.reduce((a, b) => a + b, 0))
);
const totalNetFmt = computed(() =>
  formatAmt(
    orderChartPay.value.reduce((a, b) => a + b, 0) -
      orderChartRefund.value.reduce((a, b) => a + b, 0)
  )
);
const totalFirstPayFmt = computed(() =>
  formatAmt(orderChartFirstPay.value.reduce((a, b) => a + b, 0))
);
const totalRenewFmt = computed(() =>
  formatAmt(orderChartRenew.value.reduce((a, b) => a + b, 0))
);

/** 近40日天数（用于计算日均） */
const avgDays = computed(() => orderChartPay.value.length || 40);
/* 近20日均值格式化计算属性 */
const avgPayFmt = computed(() =>
  formatAmt(orderChartPay.value.reduce((a, b) => a + b, 0) / avgDays.value)
);
const avgRefundFmt = computed(() =>
  formatAmt(orderChartRefund.value.reduce((a, b) => a + b, 0) / avgDays.value)
);
const avgNetFmt = computed(() =>
  formatAmt(
    (orderChartPay.value.reduce((a, b) => a + b, 0) -
      orderChartRefund.value.reduce((a, b) => a + b, 0)) /
      avgDays.value
  )
);
const avgFirstPayFmt = computed(() =>
  formatAmt(orderChartFirstPay.value.reduce((a, b) => a + b, 0) / avgDays.value)
);
const avgRenewFmt = computed(() =>
  formatAmt(orderChartRenew.value.reduce((a, b) => a + b, 0) / avgDays.value)
);

// ========================
// 应用日收款折线图
// ========================

/** 当前选中的应用 ID（默认 259） */
const selectedAppId = ref(259);
/** 应用图表加载状态 */
const appChartLoading = ref(false);
/** 应用图表 X 轴日期 */
const appChartDates = ref([]);
/** 应用收款金额数据 */
const appChartPay = ref([]);
/** 应用退款金额数据 */
const appChartRefund = ref([]);
/** 应用首购金额数据 */
const appChartFirstPay = ref([]);
/** 应用续订金额数据 */
const appChartRenew = ref([]);
/** 应用 T-LTV 金额数据 */
const appChartTlTv = ref([]);

/** 应用收款趋势 ECharts 配置项（模板已隐藏，保留备用） */
// eslint-disable-next-line no-unused-vars
const appChartOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    formatter: (params) =>
      params[0].axisValue +
      params
        .map(
          (p) =>
            `<br/>${p.marker}${p.seriesName}：¥${(p.value / 100).toLocaleString(
              "zh-CN",
              { minimumFractionDigits: 2 }
            )}`
        )
        .join(""),
  },
  legend: {
    data: ["收款金额", "退款金额", "初次购买", "续订", "T-LTV"],
    bottom: 0,
  },
  grid: { top: 10, left: 0, right: 8, bottom: 36, containLabel: true },
  xAxis: {
    type: "category",
    data: appChartDates.value,
    axisLabel: { fontSize: 11, rotate: 30 },
  },
  yAxis: {
    type: "value",
    axisLabel: {
      fontSize: 11,
      formatter: (v) => "¥" + (v / 100 / 10000).toFixed(1) + "万",
    },
  },
  series: [
    {
      name: "收款金额",
      type: "line",
      smooth: true,
      data: appChartPay.value,
      itemStyle: { color: "#1677ff" },
    },
    {
      name: "退款金额",
      type: "line",
      smooth: true,
      data: appChartRefund.value,
      itemStyle: { color: "#ff4d4f" },
    },
    {
      name: "初次购买",
      type: "line",
      smooth: true,
      data: appChartFirstPay.value,
      itemStyle: { color: "#52c41a" },
    },
    {
      name: "续订",
      type: "line",
      smooth: true,
      data: appChartRenew.value,
      itemStyle: { color: "#722ed1" },
    },
    {
      name: "T-LTV",
      type: "line",
      smooth: true,
      data: appChartTlTv.value,
      itemStyle: { color: "#fa8c16" },
    },
  ],
}));

/**
 * 获取指定应用的近20日收款数据
 * 接口：GET /v1/analytics/order/stats/daily/payment?appId=xxx
 */
const fetchAppChart = async () => {
  appChartLoading.value = true;
  try {
    const json = await get(analyticsUrl("/order/stats/daily/payment"), {
      pageIdx: 1,
      pageSize: 20,
      startDate: "",
      endDate: "",
      appId: selectedAppId.value,
    });
    if (json.code === 200 && json.data && json.data.data) {
      const rows = [...json.data.data].reverse();
      appChartDates.value = rows.map((r) => r.statDate.slice(5));
      appChartPay.value = rows.map((r) => r.payAmount);
      appChartRefund.value = rows.map((r) => r.refundAmount);
      appChartFirstPay.value = rows.map((r) => r.firstPayAmount);
      appChartRenew.value = rows.map((r) => r.renewAmount);
      appChartTlTv.value = rows.map((r) => r.tlTvPayAmount);
    }
  } catch (e) {
    message.error("应用收款趋势数据获取失败");
  } finally {
    appChartLoading.value = false;
  }
};

// ========================
// 每日总订单折线图
// ========================

/** 订单趋势图加载状态 */
const orderChartLoading = ref(false);
/** 图表 X 轴日期数组 */
const orderChartDates = ref([]);
/** 每日收款金额数据（分） */
const orderChartPay = ref([]);
/** 每日退款金额数据（分） */
const orderChartRefund = ref([]);

/** 每日收-退款净额（分） */
const orderChartNet = computed(() =>
  orderChartPay.value.map((v, i) => v - (orderChartRefund.value[i] || 0))
);

/** 过去7日收款均值（分）——不含今天 */
const payAvg7d = computed(() => {
  const arr = orderChartPay.value;
  if (arr.length < 2) return 0;
  const last7 = arr.slice(-8, -1);
  return last7.reduce((a, b) => a + b, 0) / last7.length;
});
/** 过去7日退款均值（分）——不含今天 */
const refundAvg7d = computed(() => {
  const arr = orderChartRefund.value;
  if (arr.length < 2) return 0;
  const last7 = arr.slice(-8, -1);
  return last7.reduce((a, b) => a + b, 0) / last7.length;
});
/** 过去7日收-退均值（分）——不含今天 */
const netAvg7d = computed(() => {
  const arr = orderChartNet.value;
  if (arr.length < 2) return 0;
  const last7 = arr.slice(-8, -1);
  return last7.reduce((a, b) => a + b, 0) / last7.length;
});

/** 整体收款趋势 ECharts 配置项 */
const orderChartOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "line" },
    formatter: (params) =>
      params[0].axisValue +
      params
        .map(
          (p) =>
            `<br/>${p.marker}${p.seriesName}：¥${(p.value / 100).toLocaleString(
              "zh-CN",
              { minimumFractionDigits: 2 }
            )}`
        )
        .join(""),
  },
  legend: { data: ["收款金额", "退款金额", "收-退款"], bottom: 0 },
  grid: { top: 10, left: 0, right: 8, bottom: 36, containLabel: true },
  xAxis: {
    type: "category",
    data: orderChartDates.value,
    axisLabel: { fontSize: 11, rotate: 30 },
  },
  yAxis: {
    type: "value",
    axisLabel: {
      fontSize: 11,
      formatter: (v) => "¥" + (v / 100 / 10000).toFixed(0) + "万",
    },
  },
  series: [
    {
      name: "收款金额",
      type: "line",
      smooth: true,
      data: orderChartPay.value,
      itemStyle: { color: "#1677ff" },
      markLine: {
        silent: true,
        symbol: "none",
        lineStyle: { type: "dashed", width: 1, color: "#1677ff" },
        label: {
          position: "insideEndTop",
          fontSize: 10,
          formatter: () =>
            "7日均 ¥" + (payAvg7d.value / 100 / 10000).toFixed(2) + "万",
        },
        data: [{ yAxis: payAvg7d.value }],
      },
    },
    {
      name: "退款金额",
      type: "line",
      smooth: true,
      data: orderChartRefund.value,
      itemStyle: { color: "#ff4d4f" },
      markLine: {
        silent: true,
        symbol: "none",
        lineStyle: { type: "dashed", width: 1, color: "#ff4d4f" },
        label: {
          position: "insideEndBottom",
          fontSize: 10,
          formatter: () =>
            "7日均 ¥" + (refundAvg7d.value / 100 / 10000).toFixed(2) + "万",
        },
        data: [{ yAxis: refundAvg7d.value }],
      },
    },
    {
      name: "收-退款",
      type: "line",
      smooth: true,
      data: orderChartNet.value,
      itemStyle: { color: "#52c41a" },
      markLine: {
        silent: true,
        symbol: "none",
        lineStyle: { type: "dashed", width: 1, color: "#52c41a" },
        label: {
          position: "insideEndTop",
          fontSize: 10,
          formatter: () =>
            "7日均 ¥" + (netAvg7d.value / 100 / 10000).toFixed(2) + "万",
        },
        data: [{ yAxis: netAvg7d.value }],
      },
    },
  ],
}));

/**
 * 获取近20日每日总订单数据（同时填充今日/昨日卡片数据）
 * 接口：GET /v1/analytics/order/stats/daily/total/payment
 * 返回数据按日期降序，第0条为今日，第1条为昨日
 */
const fetchOrderChart = async () => {
  orderChartLoading.value = true;
  try {
    const allData = [];
    for (let page = 1; page <= 2; page++) {
      const json = await get(analyticsUrl("/order/stats/daily/total/payment"), {
        pageIdx: page,
        pageSize: 20,
        startDate: "",
        endDate: "",
      });
      if (json.code === 200 && json.data && json.data.data) {
        allData.push(...json.data.data);
      } else {
        break;
      }
    }
    if (allData.length > 0) {
      if (allData.length > 0) {
        todayPayAmount.value = allData[0].payAmount;
        todayRefundAmount.value = allData[0].refundAmount;
        todayFirstPayAmount.value = allData[0].firstPayAmount;
        todayRenewAmount.value = allData[0].renewAmount;
      }
      if (allData.length > 1) {
        yestPayAmount.value = allData[1].payAmount;
        yestRefundAmount.value = allData[1].refundAmount;
        yestFirstPayAmount.value = allData[1].firstPayAmount;
        yestRenewAmount.value = allData[1].renewAmount;
      }
      const rows = [...allData].reverse();
      orderChartDates.value = rows.map((r) => r.statDate.slice(5));
      orderChartPay.value = rows.map((r) => r.payAmount);
      orderChartRefund.value = rows.map((r) => r.refundAmount);
      orderChartFirstPay.value = rows.map((r) => r.firstPayAmount);
      orderChartRenew.value = rows.map((r) => r.renewAmount);
    }
  } catch (e) {
    message.error("订单趋势数据获取失败");
  } finally {
    orderChartLoading.value = false;
  }
};

// ========================
// 商户每日收款折线图
// ========================

const merchantChartLoading = ref(false);
const merchantChartDateKeys = ref([]);
const merchantChartDates = ref([]);
const merchantChartMerchants = ref([]);
const merchantList = ref([]);

const MERCHANT_CHART_DAY_OPTIONS = [3, 7, 14, 30, 60];
const MERCHANT_CHART_DAYS_CACHE_KEY = "dashboard_merchant_chart_days";
const MERCHANT_METRIC_VISIBLE_CACHE_KEY = "dashboard_merchant_metric_visible";
const MERCHANT_VISIBLE_CACHE_KEY = "dashboard_merchant_visible";
const merchantChartMetrics = [
  { key: "pay", label: "收款金额", color: "#1677ff" },
  { key: "refund", label: "退款金额", color: "#ff4d4f" },
  { key: "net", label: "收-退款", color: "#52c41a" },
];
const merchantLineTypes = ["solid", "dashed", "dotted"];
const merchantColors = [
  "#1677ff",
  "#fa8c16",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#52c41a",
  "#2f54eb",
  "#faad14",
];
const MERCHANT_BAR_OPACITY = 0.42;

const cleanMerchantText = (value) => String(value ?? "").trim();

const normalizePaymentMerchant = (merchant) => ({
  ...merchant,
  name:
    cleanMerchantText(merchant.companyName) ||
    cleanMerchantText(merchant.company_name) ||
    cleanMerchantText(merchant.name) ||
    `公司主体${merchant.id}`,
  displayName:
    cleanMerchantText(merchant.companyName) ||
    cleanMerchantText(merchant.company_name) ||
    cleanMerchantText(merchant.name) ||
    `公司主体${merchant.id}`,
  companyName:
    cleanMerchantText(merchant.companyName) ||
    cleanMerchantText(merchant.company_name) ||
    cleanMerchantText(merchant.name) ||
    `公司主体${merchant.id}`,
  merchantIds: Array.isArray(merchant.merchantIds)
    ? merchant.merchantIds
    : Array.isArray(merchant.merchant_ids)
      ? merchant.merchant_ids
      : [],
});

const createDefaultMetricVisible = () =>
  merchantChartMetrics.reduce((acc, metric) => {
    acc[metric.label] = true;
    return acc;
  }, {});

const readJsonCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key));
    return cached && typeof cached === "object" ? cached : null;
  } catch {
    return null;
  }
};

const readMerchantMetricVisibleCache = () => {
  const cached = readJsonCache(MERCHANT_METRIC_VISIBLE_CACHE_KEY);
  if (!cached) return null;
  return merchantChartMetrics.reduce((acc, metric) => {
    acc[metric.label] = cached[metric.label] !== false;
    return acc;
  }, {});
};

const readMerchantVisibleCache = () => readJsonCache(MERCHANT_VISIBLE_CACHE_KEY);

const readMerchantChartDaysCache = () => {
  try {
    const cachedDays = Number(localStorage.getItem(MERCHANT_CHART_DAYS_CACHE_KEY));
    return MERCHANT_CHART_DAY_OPTIONS.includes(cachedDays) ? cachedDays : null;
  } catch {
    return null;
  }
};

const merchantChartDays = ref(readMerchantChartDaysCache() || 30);
const merchantMetricVisible = ref(
  readMerchantMetricVisibleCache() || createDefaultMetricVisible()
);
const merchantMetricNames = merchantChartMetrics.map((metric) => metric.label);
const savedMerchantVisible = readMerchantVisibleCache();
const merchantVisible = ref(savedMerchantVisible || {});

const normalizeMerchantId = (merchantId) => String(merchantId ?? "");

const isMerchantVisible = (merchantId) =>
  merchantVisible.value[normalizeMerchantId(merchantId)] !== false;

const toggleMerchant = (merchantId) => {
  const merchantKey = normalizeMerchantId(merchantId);
  const nextVisible = {
    ...merchantVisible.value,
    [merchantKey]: !isMerchantVisible(merchantId),
  };
  merchantVisible.value = nextVisible;
  localStorage.setItem(MERCHANT_VISIBLE_CACHE_KEY, JSON.stringify(nextVisible));
};

const ensureMerchantVisible = (merchants) => {
  const nextVisible = {};
  for (const merchant of merchants) {
    const merchantKey = normalizeMerchantId(merchant.id);
    nextVisible[merchantKey] = merchantVisible.value[merchantKey] ?? true;
  }
  merchantVisible.value = nextVisible;
  localStorage.setItem(MERCHANT_VISIBLE_CACHE_KEY, JSON.stringify(nextVisible));
};

const merchantLineType = (merchantId) => {
  const merchantIndex = merchantList.value.findIndex(
    (merchant) => normalizeMerchantId(merchant.id) === normalizeMerchantId(merchantId)
  );
  const safeIndex = merchantIndex >= 0 ? merchantIndex : 0;
  return merchantLineTypes[safeIndex % merchantLineTypes.length];
};

const merchantColor = (merchantId) => {
  const merchantIndex = merchantList.value.findIndex(
    (merchant) => normalizeMerchantId(merchant.id) === normalizeMerchantId(merchantId)
  );
  const safeIndex = merchantIndex >= 0 ? merchantIndex : 0;
  return merchantColors[safeIndex % merchantColors.length];
};

const merchantTagLineStyle = (merchantId) => ({
  borderTopStyle: merchantLineType(merchantId),
  borderTopColor: merchantColor(merchantId),
});

const merchantTagDotStyle = (merchantId) => ({
  backgroundColor: merchantColor(merchantId),
  opacity: MERCHANT_BAR_OPACITY,
});

const buildMerchantDateKeys = () =>
  Array.from({ length: merchantChartDays.value }, (_, index) =>
    getOffsetDateString(index - (merchantChartDays.value - 1))
  );

const getMerchantMetricData = (merchant, metricKey) => {
  if (metricKey === "pay") return merchant.pay;
  if (metricKey === "refund") return merchant.refund;
  return merchant.pay.map((value, index) => value - (merchant.refund[index] || 0));
};

const formatMerchantPaySummaryAmount = (fen) =>
  (fen / 100 / 10000).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMerchantPaySummaryCount = (count) =>
  Number(count || 0).toLocaleString("zh-CN");

const formatMerchantTooltipAmount = (fen) =>
  (fen / 100 / 10000).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const merchantPaySummaryCompanies = computed(() =>
  merchantList.value.map((merchant) => ({
    id: normalizeMerchantId(merchant.id),
    label: merchant.displayName,
  }))
);

const getMerchantPaySummaryMetrics = (merchantId, dateKey) => {
  const dateIndex = merchantChartDateKeys.value.indexOf(dateKey);
  if (dateIndex < 0) {
    return {
      amountFen: 0,
      firstPayOrderCount: 0,
      renewOrderCount: 0,
      payReportOrderCount: 0,
    };
  }
  const merchant = merchantChartMerchants.value.find(
    (merchant) => normalizeMerchantId(merchant.id) === normalizeMerchantId(merchantId)
  );
  return {
    amountFen: Number(merchant?.pay?.[dateIndex] || 0),
    firstPayOrderCount: Number(merchant?.firstPayOrderCounts?.[dateIndex] || 0),
    renewOrderCount: Number(merchant?.renewOrderCounts?.[dateIndex] || 0),
    payReportOrderCount: Number(merchant?.payReportOrderCounts?.[dateIndex] || 0),
  };
};

const formatMerchantPaySummaryMetrics = (metrics) => ({
  amount: formatMerchantPaySummaryAmount(metrics.amountFen),
  firstPayOrderCount: formatMerchantPaySummaryCount(metrics.firstPayOrderCount),
  renewOrderCount: formatMerchantPaySummaryCount(metrics.renewOrderCount),
  payReportOrderCount: formatMerchantPaySummaryCount(metrics.payReportOrderCount),
});

const merchantPaySummaryRows = computed(() =>
  [
    { key: "today", label: "今日", dateKey: getOffsetDateString(0) },
    { key: "yesterday", label: "昨日", dateKey: getOffsetDateString(-1) },
    { key: "dayBeforeYesterday", label: "前天", dateKey: getOffsetDateString(-2) },
  ].map((row) => {
    const totalMetrics = {
      amountFen: 0,
      firstPayOrderCount: 0,
      renewOrderCount: 0,
      payReportOrderCount: 0,
    };
    const cells = merchantPaySummaryCompanies.value.reduce((acc, company) => {
      const metrics = getMerchantPaySummaryMetrics(company.id, row.dateKey);
      acc[company.id] = formatMerchantPaySummaryMetrics(metrics);
      totalMetrics.amountFen += metrics.amountFen;
      totalMetrics.firstPayOrderCount += metrics.firstPayOrderCount;
      totalMetrics.renewOrderCount += metrics.renewOrderCount;
      totalMetrics.payReportOrderCount += metrics.payReportOrderCount;
      return acc;
    }, {});
    return {
      ...row,
      cells,
      total: formatMerchantPaySummaryMetrics(totalMetrics),
    };
  })
);

const merchantChartHasData = computed(() =>
  merchantChartMerchants.value.some((merchant) => merchant.hasRows)
);

const merchantMetricLegendSeries = computed(() =>
  merchantChartMetrics.map((metric) => ({
    name: metric.label,
    type: "line",
    data: [],
    showSymbol: false,
    silent: true,
    itemStyle: { color: metric.color },
    lineStyle: { color: metric.color },
    tooltip: { show: false },
  }))
);

const merchantBarSeries = computed(() => {
  if (merchantMetricVisible.value["收款金额"] !== false) {
    return merchantChartMerchants.value
      .filter((merchant) => isMerchantVisible(merchant.id))
      .map((merchant) => ({
        name: `${merchant.displayName} - 收款金额`,
        type: "bar",
        data: merchant.pay,
        barMaxWidth: 18,
        itemStyle: { color: merchantColor(merchant.id), opacity: MERCHANT_BAR_OPACITY },
        emphasis: { focus: "series" },
        z: 1,
      }));
  }
  return [];
});

const merchantVisibleSeries = computed(() =>
  merchantChartMerchants.value.flatMap((merchant) => {
    if (!isMerchantVisible(merchant.id)) return [];
    return merchantChartMetrics
      .filter(
        (metric) =>
          metric.key !== "pay" && merchantMetricVisible.value[metric.label] !== false
      )
      .map((metric) => ({
        name: `${merchant.displayName} - ${metric.label}`,
        type: "line",
        smooth: true,
        showSymbol: false,
        data: getMerchantMetricData(merchant, metric.key),
        itemStyle: { color: metric.color },
        lineStyle: {
          color: metric.color,
          width: 2,
          type: merchantLineType(merchant.id),
        },
        emphasis: { focus: "series" },
        z: 3,
      }));
  })
);

const onMerchantMetricLegendSelectChanged = ({ selected }) => {
  merchantMetricVisible.value = merchantChartMetrics.reduce((acc, metric) => {
    acc[metric.label] = selected?.[metric.label] !== false;
    return acc;
  }, {});
  localStorage.setItem(
    MERCHANT_METRIC_VISIBLE_CACHE_KEY,
    JSON.stringify(merchantMetricVisible.value)
  );
};

const buildMerchantSeriesData = (merchant, rows) => {
  const rowsByDate = new Map(rows.map((row) => [row.statDate, row]));
  const firstRow = rows[0] || {};
  const displayName =
    merchant.displayName ||
    cleanMerchantText(firstRow.companyName) ||
    cleanMerchantText(firstRow.merchantName) ||
    `公司主体${merchant.id}`;
  return {
    id: merchant.id,
    name: displayName,
    displayName,
    companyName: merchant.companyName || displayName,
    merchantIds: merchant.merchantIds || [],
    pay: merchantChartDateKeys.value.map((date) => rowsByDate.get(date)?.payAmount || 0),
    refund: merchantChartDateKeys.value.map(
      (date) => rowsByDate.get(date)?.refundAmount || 0
    ),
    firstPayOrderCounts: merchantChartDateKeys.value.map(
      (date) => rowsByDate.get(date)?.firstPayOrderCount || 0
    ),
    renewOrderCounts: merchantChartDateKeys.value.map(
      (date) => rowsByDate.get(date)?.renewOrderCount || 0
    ),
    payReportOrderCounts: merchantChartDateKeys.value.map(
      (date) => rowsByDate.get(date)?.payReportOrderCount || 0
    ),
    hasRows: rows.length > 0,
  };
};

const HTML_ESCAPE_MAP = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char]);

const merchantChartOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "line" },
    formatter: (params) => {
      const visibleParams = params.filter(
        (p) => !merchantMetricNames.includes(p.seriesName) && p.value != null
      );
      return (
        (params[0]?.axisValue || "") +
        visibleParams
          .map(
            (p) =>
              `<br/>${p.marker}${escapeHtml(
                p.seriesName
              )}：¥${formatMerchantTooltipAmount(p.value)}万`
          )
          .join("")
      );
    },
  },
  legend: {
    data: merchantChartMetrics.map((metric) => metric.label),
    selected: merchantMetricVisible.value,
    bottom: 0,
  },
  grid: { top: 10, left: 0, right: 8, bottom: 36, containLabel: true },
  xAxis: {
    type: "category",
    data: merchantChartDates.value,
    axisLabel: { fontSize: 11, rotate: 30 },
  },
  yAxis: {
    type: "value",
    axisLabel: {
      fontSize: 11,
      formatter: (v) => "¥" + (v / 100 / 10000).toFixed(0) + "万",
    },
  },
  series: [
    ...merchantMetricLegendSeries.value,
    ...merchantBarSeries.value,
    ...merchantVisibleSeries.value,
  ],
}));

const fetchMerchantChart = async () => {
  merchantChartLoading.value = true;
  try {
    const dateKeys = buildMerchantDateKeys();
    merchantChartDateKeys.value = dateKeys;
    merchantChartDates.value = dateKeys.map((date) => date.slice(5));
    ensureMerchantVisible(merchantList.value);

    const merchantGroups = await Promise.all(
      merchantList.value.map(async (merchant) => {
        const rows = await fetchAllPages(
          analyticsUrl("/order/stats/daily/merchant/payment"),
          {
            startDate: dateKeys[0],
            endDate: dateKeys[dateKeys.length - 1],
            merchantId: merchant.id,
          }
        );
        return buildMerchantSeriesData(merchant, rows);
      })
    );
    merchantChartMerchants.value = merchantGroups;
  } catch (e) {
    message.error("商户收款趋势数据获取失败");
  } finally {
    merchantChartLoading.value = false;
  }
};

const fetchMerchantList = async () => {
  try {
    const json = await get(analyticsUrl("/merchant/payment/list"));
    merchantList.value = Array.isArray(json.data?.data)
      ? json.data.data.map(normalizePaymentMerchant)
      : [];
    ensureMerchantVisible(merchantList.value);
  } catch (e) {
    merchantList.value = [];
    ensureMerchantVisible([]);
    message.error("商户列表获取失败");
  }
};

watch(merchantChartDays, () => {
  localStorage.setItem(MERCHANT_CHART_DAYS_CACHE_KEY, String(merchantChartDays.value));
  if (!merchantList.value.length) return;
  fetchMerchantChart();
});

// ========================
// LTV 留存率热力表
// ========================

const retentionLoading = ref(false);
const RETENTION_DIM_KEY = "dashboard_retention_dim";
const RETENTION_GROUP_KEY = "dashboard_retention_group";
const retentionDimensionId = ref(undefined);
const retentionDimOptions = ref([]);
const retentionRawData = ref([]);
const retentionGroupBy = ref(
  localStorage.getItem(RETENTION_GROUP_KEY) || "day"
);

/** 计算所有行中最大的 mNo */
const retentionMaxM = computed(() =>
  retentionRawData.value.reduce(
    (max, r) => Math.max(max, ...(r.stages ?? []).map((s) => s.mNo)),
    0
  )
);

/**
 * 获取日期所属的日历周一（周一为一周的起始）
 */
const getMonday = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

/**
 * 将每日原始行转为统一结构（含 stages 的 orderCount）
 */
const retentionDailyRows = computed(() =>
  [...retentionRawData.value].reverse().map((r) => {
    const counts = {};
    const rates = {};
    for (const s of r.stages ?? []) {
      if (s.mNo >= 1) {
        counts[s.mNo] = s.orderCount ?? 0;
        const val = parseFloat(s.retentionRate);
        rates[s.mNo] = isNaN(val) ? null : +(val * 100).toFixed(1);
      }
    }
    return {
      statDate: r.statDate,
      label: r.statDate,
      m0Count: r.m0OrderCount ?? 0,
      counts,
      rates,
    };
  })
);

/**
 * 按分组策略聚合行
 * 周/月：对同组日期的 m0Count 和各 mNo 的 orderCount 求和，重算留存率
 */
const groupRows = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r.statDate);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  const result = [];
  for (const [, group] of map) {
    const m0Sum = group.reduce((s, r) => s + r.m0Count, 0);
    const maxN = retentionMaxM.value;
    const counts = {};
    const rates = {};
    for (let n = 1; n <= maxN; n++) {
      const cntSum = group.reduce((s, r) => s + (r.counts[n] ?? 0), 0);
      counts[n] = cntSum;
      const hasData = group.some((r) => r.counts[n] != null);
      // 周/月留存率: 均以 M0 订单数为分母 (Mn 订单数 / M0 订单数)
      const base = m0Sum;
      rates[n] =
        hasData && base > 0 ? +((cntSum / base) * 100).toFixed(1) : null;
    }
    result.push({
      label: labelFn(group),
      m0Count: m0Sum,
      counts,
      rates,
    });
  }
  return result;
};

/** 最终展示的表格行（支持 日/周/月 分组） */
const retentionRows = computed(() => {
  const daily = retentionDailyRows.value;
  let rows;
  if (retentionGroupBy.value === "week") {
    rows = groupRows(
      daily,
      (d) => getMonday(d),
      (g) => {
        const mon = getMonday(g[0].statDate);
        const sun = new Date(mon);
        sun.setDate(sun.getDate() + 6);
        const sunStr = sun.toISOString().slice(0, 10);
        return mon + "~" + sunStr;
      }
    );
  } else if (retentionGroupBy.value === "month") {
    rows = groupRows(
      daily,
      (d) => d.slice(0, 7),
      (g) => g[0].statDate.slice(0, 7)
    );
  } else {
    rows = daily;
  }
  return rows.filter(
    (r) =>
      r.m0Count > 0 || Object.values(r.rates).some((v) => v != null && v !== 0)
  );
});

/**
 * 留存率单元格背景色
 * 蓝色渐变：留存率越高颜色越深
 */
const retentionCellStyle = (rate) => {
  if (rate == null || rate === 0) return {};
  // rate 范围 0~100，映射为透明度 0.05~0.6
  const alpha = Math.min(0.05 + (rate / 100) * 0.55, 0.65);
  const textColor = alpha > 0.35 ? "#fff" : "#333";
  return {
    background: `rgba(24, 144, 255, ${alpha})`,
    color: textColor,
  };
};

/** 获取维度列表，并从缓存恢复上次选择或默认选中第一个 */
const fetchRetentionDimensions = async () => {
  try {
    const json = await get(analyticsUrl("/order/stats/cohort/dimension/list"));
    const arr = json.data?.data;
    if (Array.isArray(arr)) {
      retentionDimOptions.value = arr
        .filter((d) => d.isEnabled)
        .filter((d) => {
          const allowed = getAllowedDimensionIds();
          return allowed.length === 0 || allowed.includes(d.id);
        })
        .map((d) => ({ label: d.name, value: d.id }));
      // 从缓存恢复，或默认选中第一个
      const cached = localStorage.getItem(RETENTION_DIM_KEY);
      const cachedId = cached != null ? Number(cached) : null;
      if (
        cachedId &&
        retentionDimOptions.value.some((o) => o.value === cachedId)
      ) {
        retentionDimensionId.value = cachedId;
      } else if (retentionDimOptions.value.length > 0) {
        retentionDimensionId.value = retentionDimOptions.value[0].value;
      }
    }
  } catch {
    // 静默
  }
};

/** 维度切换：保存到缓存并重新加载 */
const onRetentionDimChange = (val) => {
  localStorage.setItem(RETENTION_DIM_KEY, String(val));
  fetchRetentionData();
};

/** 获取留存数据（复用 LTV-V2 接口） */
const fetchRetentionData = async () => {
  retentionLoading.value = true;
  try {
    const params = { pageIdx: 1, pageSize: 300, startDate: "", endDate: "" };
    if (retentionDimensionId.value != null)
      params.dimensionId = retentionDimensionId.value;
    const json = await get(
      analyticsUrl("/order/stats/cohort/v2/daily/report"),
      params
    );
    if (json.code === 200 && json.data) {
      retentionRawData.value = json.data.data ?? [];
    }
  } catch {
    message.error("留存数据获取失败");
  } finally {
    retentionLoading.value = false;
  }
};

// ====================================
// 通用：分页全量拉取
// ====================================

/**
 * 自动分页拉取所有数据：先取第1页，根据 pageCount 并发拉取剩余页，合并 data 数组返回。
 */
const fetchAllPages = async (url, params) => {
  const PAGE_SIZE = 300;
  const first = await get(url, { ...params, pageIdx: 1, pageSize: PAGE_SIZE });
  if (first.code !== 200 || !first.data) return [];
  const all = [...(first.data.data ?? [])];
  const pageCount = first.data.pageCount ?? 1;
  if (pageCount > 1) {
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) =>
        get(url, { ...params, pageIdx: i + 2, pageSize: PAGE_SIZE })
      )
    );
    for (const json of rest) {
      if (json.code === 200 && json.data?.data) {
        all.push(...json.data.data);
      }
    }
  }
  return all;
};

// ====================================
// 渠道留存热力表（支付渠道 Cohort）
// ====================================

const chLoading = ref(false);
const CH_GROUP_KEY = "dash_channel_retention_group";
const CH_PAY_TYPE_KEY = "dash_channel_retention_pay_type";
const chRawData = ref([]);
const chGroupBy = ref(localStorage.getItem(CH_GROUP_KEY) || "day");
const chPayType = ref(localStorage.getItem(CH_PAY_TYPE_KEY) || "kq");

const chMaxM = computed(() =>
  chRawData.value.reduce(
    (max, r) => Math.max(max, ...(r.stages ?? []).map((s) => s.mNo)),
    0
  )
);

const chDailyRows = computed(() =>
  [...chRawData.value].reverse().map((r) => {
    const counts = {};
    const rates = {};
    for (const s of r.stages ?? []) {
      if (s.mNo >= 1) {
        counts[s.mNo] = s.orderCount ?? 0;
        const val = parseFloat(s.retentionRate);
        rates[s.mNo] = isNaN(val) ? null : +(val * 100).toFixed(1);
      }
    }
    return {
      statDate: r.statDate,
      label: r.statDate,
      payType: r.payType ?? "",
      m0Count: r.m0OrderCount ?? 0,
      counts,
      rates,
    };
  })
);

const chGroupRows = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r.statDate) + "_" + r.payType;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  const result = [];
  for (const [, group] of map) {
    const m0Sum = group.reduce((s, r) => s + r.m0Count, 0);
    const maxN = chMaxM.value;
    const counts = {};
    const rates = {};
    for (let n = 1; n <= maxN; n++) {
      const cntSum = group.reduce((s, r) => s + (r.counts[n] ?? 0), 0);
      counts[n] = cntSum;
      const hasData = group.some((r) => r.counts[n] != null);
      // 周/月留存率均以 M0 订单数为分母
      const base = m0Sum;
      rates[n] = hasData && base > 0 ? +((cntSum / base) * 100).toFixed(1) : null;
    }
    result.push({ label: labelFn(group), payType: group[0].payType, m0Count: m0Sum, counts, rates });
  }
  return result;
};

const chRows = computed(() => {
  const daily = chDailyRows.value;
  let rows;
  if (chGroupBy.value === "week") {
    rows = chGroupRows(daily, (d) => getMonday(d), (g) => {
      const mon = getMonday(g[0].statDate);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return mon + "~" + sun.toISOString().slice(0, 10);
    });
  } else if (chGroupBy.value === "month") {
    rows = chGroupRows(daily, (d) => d.slice(0, 7), (g) => g[0].statDate.slice(0, 7));
  } else {
    rows = daily;
  }
  return rows.filter((r) => r.m0Count > 0 || Object.values(r.rates).some((v) => v != null && v !== 0));
});

const fetchChData = async () => {
  chLoading.value = true;
  try {
    const params = {};
    if (chPayType.value) params.payType = chPayType.value;
    const [dailyRows, stageRows] = await Promise.all([
      fetchAllPages(analyticsUrl("/order/stats/cohort/channel/daily/report"), params),
      fetchAllPages(analyticsUrl("/order/stats/cohort/channel/daily/stage/report"), params),
    ]);
    // group stage records by statDate+payType
    const stageMap = new Map();
    for (const s of stageRows) {
      const key = `${s.statDate}_${s.payType}`;
      if (!stageMap.has(key)) stageMap.set(key, []);
      stageMap.get(key).push(s);
    }
    chRawData.value = dailyRows.map((r) => ({
      ...r,
      stages: stageMap.get(`${r.statDate}_${r.payType}`) ?? r.stages ?? [],
    }));
  } catch {
    message.error("渠道留存数据加载失败");
  } finally {
    chLoading.value = false;
  }
};

watch(chGroupBy, (v) => { localStorage.setItem(CH_GROUP_KEY, v); });
watch(chPayType, (v) => { localStorage.setItem(CH_PAY_TYPE_KEY, v); });

// ====================================
// 银行留存热力表（支付渠道+银行 Cohort）
// ====================================

const bkLoading = ref(false);
const BK_GROUP_KEY = "dash_bank_retention_group";
const BK_PAY_TYPE_KEY = "dash_bank_retention_pay_type";
const BK_BANK_CODE_KEY = "dash_bank_retention_bank_code";
const bkRawData = ref([]);
const bkGroupBy = ref(localStorage.getItem(BK_GROUP_KEY) || "day");
const bkPayType = ref(localStorage.getItem(BK_PAY_TYPE_KEY) || "");
const bkBankCode = ref(localStorage.getItem(BK_BANK_CODE_KEY) || undefined);
const PAY_TYPE_LABEL_MAP = {
  kq: "快钱",
  hlb: "合利宝",
  jd: "京东",
};

const BANK_NAME_MAP = {
  ABC: "中国农业银行",
  BOC: "中国银行",
  BOCO: "交通银行",
  CCB: "中国建设银行",
  CEB: "中国光大银行",
  CGB: "广发银行",
  CIB: "兴业银行",
  CMBC: "中国民生银行",
  CMBCHINA: "招商银行",
  ECITIC: "中信银行",
  HXB: "华夏银行",
  ICBC: "中国工商银行",
  PINGAN: "平安银行",
  POST: "中国邮政储蓄银行",
  GDB: "广发银行",
  PAB: "平安银行",
  PSBC: "中国邮政储蓄银行",
  SPDB: "浦发银行",
};

const BANK_CONFIG = [
  { payType: "hlb", bankCode: "ABC" },
  { payType: "hlb", bankCode: "BOC" },
  { payType: "hlb", bankCode: "BOCO" },
  { payType: "hlb", bankCode: "CCB" },
  { payType: "hlb", bankCode: "CEB" },
  { payType: "hlb", bankCode: "CGB" },
  { payType: "hlb", bankCode: "CIB" },
  { payType: "hlb", bankCode: "CMBC" },
  { payType: "hlb", bankCode: "CMBCHINA" },
  { payType: "hlb", bankCode: "ECITIC" },
  { payType: "hlb", bankCode: "HXB" },
  { payType: "hlb", bankCode: "ICBC" },
  { payType: "hlb", bankCode: "PINGAN" },
  { payType: "hlb", bankCode: "POST" },
  { payType: "kq", bankCode: "ABC" },
  { payType: "kq", bankCode: "BOC" },
  { payType: "kq", bankCode: "CCB" },
  { payType: "kq", bankCode: "CMBC" },
  { payType: "kq", bankCode: "GDB" },
  { payType: "kq", bankCode: "HXB" },
  { payType: "kq", bankCode: "PAB" },
  { payType: "kq", bankCode: "PSBC" },
  { payType: "kq", bankCode: "SPDB" },
];

const bkBankOptions = computed(() => {
  const codeSet = new Set(BANK_CONFIG.map((b) => b.bankCode));
  return [...codeSet]
    .sort((a, b) => a.localeCompare(b))
    .map((code) => ({
      label: BANK_NAME_MAP[code] ? `${BANK_NAME_MAP[code]}(${code})` : code,
      value: code,
    }));
});

const payTypeRank = (payType) => {
  if (payType === "kq") return 1;
  if (payType === "hlb") return 2;
  if (payType === "jd") return 3;
  return 9;
};

const getPayTypeLabel = (payType) => PAY_TYPE_LABEL_MAP[payType] || payType || "未知渠道";

const BK_ROW_BG = {
  kq: { row: "#e6f0ff", meta: "#bfdbff", channelText: "#0958d9", bar: "#1677ff" },
  hlb: { row: "#e8f7e1", meta: "#c8eab3", channelText: "#389e0d", bar: "#52c41a" },
  jd: { row: "#fff2d9", meta: "#ffe0a3", channelText: "#d48806", bar: "#faad14" },
  default: { row: "#f0f0f0", meta: "#d9d9d9", channelText: "rgba(0,0,0,0.7)", bar: "rgba(0,0,0,0.25)" },
};

const getBkRowClass = (payType) => {
  if (payType === "kq") return "bk-row-kq";
  if (payType === "hlb") return "bk-row-hlb";
  if (payType === "jd") return "bk-row-jd";
  return "bk-row-default";
};

const getBkColors = (payType) => BK_ROW_BG[payType] || BK_ROW_BG.default;

const getBkRowBgStyle = (payType, isMeta = false) => {
  const c = getBkColors(payType);
  return { backgroundColor: isMeta ? c.meta : c.row };
};

const getBkChannelCellStyle = (payType) => {
  const c = getBkColors(payType);
  return {
    backgroundColor: c.meta,
    color: c.channelText,
    boxShadow: `inset 4px 0 0 ${c.bar}`,
    fontWeight: 600,
  };
};

/**
 * 热力单元格样式：有留存率时使用原有蓝色渐变；
 * 无值时回退到该渠道的底色，保证整行颜色连贯。
 */
const getBkCellStyle = (row, n) => {
  const rate = row.rates[n];
  if (rate != null && rate !== 0) {
    return retentionCellStyle(rate);
  }
  const c = getBkColors(row.payType);
  return { backgroundColor: c.row };
};

const bkMaxM = computed(() =>
  bkRawData.value.reduce(
    (max, r) => Math.max(max, ...(r.stages ?? []).map((s) => s.mNo)),
    0
  )
);

const bkDailyRows = computed(() =>
  [...bkRawData.value]
    .sort((a, b) => {
      if (a.statDate === b.statDate) {
        return payTypeRank(a.payType) - payTypeRank(b.payType);
      }
      return a.statDate.localeCompare(b.statDate);
    })
    .map((r) => {
    const counts = {};
    const rates = {};
    for (const s of r.stages ?? []) {
      if (s.mNo >= 1) {
        counts[s.mNo] = s.orderCount ?? 0;
        const val = parseFloat(s.retentionRate);
        rates[s.mNo] = isNaN(val) ? null : +(val * 100).toFixed(1);
      }
    }
    return {
      statDate: r.statDate,
      dateLabel: r.statDate,
      payTypeLabel: getPayTypeLabel(r.payType),
      payType: r.payType ?? "",
      bankCode: r.bankCode ?? "",
      m0Count: r.m0OrderCount ?? 0,
      counts,
      rates,
    };
  })
);

const bkGroupRows = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r.statDate) + "_" + r.payType + "_" + r.bankCode;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  const result = [];
  for (const [, group] of map) {
    const m0Sum = group.reduce((s, r) => s + r.m0Count, 0);
    const maxN = bkMaxM.value;
    const counts = {};
    const rates = {};
    for (let n = 1; n <= maxN; n++) {
      const cntSum = group.reduce((s, r) => s + (r.counts[n] ?? 0), 0);
      counts[n] = cntSum;
      const hasData = group.some((r) => r.counts[n] != null);
      // 周/月留存率均以 M0 订单数为分母
      const base = m0Sum;
      rates[n] = hasData && base > 0 ? +((cntSum / base) * 100).toFixed(1) : null;
    }
    const labels = labelFn(group);
    result.push({
      dateLabel: labels.dateLabel,
      payTypeLabel: labels.payTypeLabel,
      payType: group[0].payType,
      bankCode: group[0].bankCode,
      m0Count: m0Sum,
      counts,
      rates,
    });
  }
  return result;
};

const bkRows = computed(() => {
  const daily = bkDailyRows.value;
  let rows;
  if (bkGroupBy.value === "week") {
    rows = bkGroupRows(daily, (d) => getMonday(d), (g) => {
      const mon = getMonday(g[0].statDate);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return {
        dateLabel: `${mon}~${sun.toISOString().slice(0, 10)}`,
        payTypeLabel: getPayTypeLabel(g[0].payType),
      };
    });
  } else if (bkGroupBy.value === "month") {
    rows = bkGroupRows(daily, (d) => d.slice(0, 7), (g) => ({
      dateLabel: g[0].statDate.slice(0, 7),
      payTypeLabel: getPayTypeLabel(g[0].payType),
    }));
  } else {
    rows = daily;
  }
  return rows.filter((r) => r.m0Count > 0 || Object.values(r.rates).some((v) => v != null && v !== 0));
});

const bkDisplayRows = computed(() => {
  const result = [];
  let currentDateLabel = "";
  let currentStartIndex = -1;

  for (const row of bkRows.value) {
    if (row.dateLabel !== currentDateLabel) {
      currentDateLabel = row.dateLabel;
      currentStartIndex = result.length;
      result.push({
        ...row,
        showDate: true,
        rowSpan: 1,
      });
      continue;
    }

    result[currentStartIndex].rowSpan += 1;
    result.push({
      ...row,
      showDate: false,
      rowSpan: 0,
    });
  }

  return result;
});

const fetchBkData = async () => {
  if (!bkBankCode.value) {
    bkRawData.value = [];
    return;
  }
  bkLoading.value = true;
  try {
    const params = {};
    if (bkPayType.value) params.payType = bkPayType.value;
    if (bkBankCode.value) params.bankCode = bkBankCode.value;
    const [dailyRows, stageRows] = await Promise.all([
      fetchAllPages(analyticsUrl("/order/stats/cohort/bank/daily/report"), params),
      fetchAllPages(analyticsUrl("/order/stats/cohort/bank/daily/stage/report"), params),
    ]);
    // group stage records by statDate+payType+bankCode
    const stageMap = new Map();
    for (const s of stageRows) {
      const key = `${s.statDate}_${s.payType}_${s.bankCode}`;
      if (!stageMap.has(key)) stageMap.set(key, []);
      stageMap.get(key).push(s);
    }
    bkRawData.value = dailyRows.map((r) => ({
      ...r,
      stages: stageMap.get(`${r.statDate}_${r.payType}_${r.bankCode}`) ?? r.stages ?? [],
    }));
  } catch {
    message.error("银行留存数据加载失败");
  } finally {
    bkLoading.value = false;
  }
};

watch(bkGroupBy, (v) => { localStorage.setItem(BK_GROUP_KEY, v); });
watch(bkPayType, (v) => { localStorage.setItem(BK_PAY_TYPE_KEY, v); });
watch(bkBankCode, (v) => { v ? localStorage.setItem(BK_BANK_CODE_KEY, v) : localStorage.removeItem(BK_BANK_CODE_KEY); });

/**
 * 控制台自动刷新：每 60 秒重新拉取当前页面的全部数据。
 * 通过目标时间计算剩余秒数，避免浏览器定时器轻微漂移导致倒计时失真。
 */
const AUTO_REFRESH_SECONDS = 60;
const refreshCountdown = ref(AUTO_REFRESH_SECONDS);
const dashboardRefreshing = ref(false);
const lastRefreshTime = ref("-");
const lastRefreshTimeIso = ref("");
let refreshCountdownTimer = null;
let nextRefreshAt = 0;
let dashboardMounted = false;

const formatRefreshTime = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const refreshDashboardData = async () => {
  if (dashboardRefreshing.value) return;
  dashboardRefreshing.value = true;
  beanLoading.value = true;
  const beanDataRequest = Promise.all([
    fetchBeanBalance(),
    fetchBeanConsume(),
  ]).finally(() => {
    beanLoading.value = false;
  });

  try {
    await Promise.all([
      beanDataRequest,
      fetchTaskRecordStat(),
      fetchOrderChart(),
      fetchMerchantList().then(() => fetchMerchantChart()),
      fetchAppChart(),
      fetchOrderStat(),
      fetchRetentionDimensions().then(() => fetchRetentionData()),
      fetchChData(),
      fetchBkData(),
    ]);
    const refreshedAt = new Date();
    lastRefreshTime.value = formatRefreshTime(refreshedAt);
    lastRefreshTimeIso.value = refreshedAt.toISOString();
  } finally {
    dashboardRefreshing.value = false;
  }
};

const scheduleNextDashboardRefresh = () => {
  nextRefreshAt = Date.now() + AUTO_REFRESH_SECONDS * 1000;
  refreshCountdown.value = AUTO_REFRESH_SECONDS;
};

const stopDashboardRefreshCountdown = () => {
  if (refreshCountdownTimer !== null) {
    window.clearInterval(refreshCountdownTimer);
    refreshCountdownTimer = null;
  }
};

const updateRefreshCountdown = async () => {
  const millisecondsLeft = nextRefreshAt - Date.now();
  if (millisecondsLeft > 0) {
    refreshCountdown.value = Math.ceil(millisecondsLeft / 1000);
    return;
  }

  refreshCountdown.value = 0;
  stopDashboardRefreshCountdown();
  try {
    await refreshDashboardData();
  } finally {
    if (dashboardMounted) startDashboardRefreshCountdown();
  }
};

const startDashboardRefreshCountdown = () => {
  stopDashboardRefreshCountdown();
  scheduleNextDashboardRefresh();
  refreshCountdownTimer = window.setInterval(updateRefreshCountdown, 1000);
};

const handleManualRefresh = async () => {
  if (dashboardRefreshing.value) return;
  stopDashboardRefreshCountdown();
  try {
    await refreshDashboardData();
  } finally {
    if (dashboardMounted) startDashboardRefreshCountdown();
  }
};

/**
 * 组件挂载：首次加载全部数据，并启动实时刷新倒计时
 */
onMounted(async () => {
  dashboardMounted = true;
  connect("dashboard-link");
  await refreshDashboardData();
  if (dashboardMounted) startDashboardRefreshCountdown();
});

onUnmounted(() => {
  dashboardMounted = false;
  stopDashboardRefreshCountdown();
});

/** 监听应用切换，重新加载应用折线图 */
watch(selectedAppId, () => {
  fetchAppChart();
});

/** 缓存日/周/月选择 */
watch(retentionGroupBy, (val) => {
  localStorage.setItem(RETENTION_GROUP_KEY, val);
});
</script>

<style scoped>
.dashboard-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  min-height: 52px;
  margin-bottom: 16px;
}

.bean-summary {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-right: auto;
  max-width: 100%;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 22px;
  font-variant-numeric: tabular-nums;
}

.bean-summary-item {
  white-space: nowrap;
}

.dashboard-refresh-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.dashboard-refresh-row {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
}

.dashboard-refresh-label {
  display: inline-block;
  width: 8.5em;
  text-align: right;
}

.refresh-countdown-value {
  display: inline-block;
  min-width: 4ch;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  text-align: left;
}

.refresh-time-value {
  color: rgba(0, 0, 0, 0.45);
  font-variant-numeric: tabular-nums;
}

.dashboard-refresh-button {
  align-self: center;
  min-width: 96px;
  height: 44px;
  border-radius: 8px;
  font-weight: 500;
}

.dashboard-refresh-button :deep(.anticon) {
  font-size: 18px;
}

@media (max-width: 576px) {
  .dashboard-toolbar {
    gap: 12px;
  }

  .dashboard-refresh-row {
    font-size: 13px;
  }

  .dashboard-refresh-button {
    min-width: 84px;
    padding-inline: 12px;
  }
}

/* 让 a-col 内的卡片撑满同行高度 */
:deep(.ant-col) {
  display: flex;
}
:deep(.ant-col) > .ant-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}
:deep(.ant-col) > .ant-card > .ant-card-body {
  flex: 1;
}

.chart-today-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
  padding: 6px 0 0;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

.custom-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
  font-size: 12px;
}
.custom-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  color: rgba(0, 0, 0, 0.75);
  transition: opacity 0.2s;
}
.custom-legend-item.is-off {
  opacity: 0.35;
  text-decoration: line-through;
}
.custom-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.channel-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.channel-hint-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.merchant-card-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px;
}

.merchant-card-title-text {
  flex: 0 0 auto;
}

.merchant-day-switch {
  flex: 0 0 auto;
}

.merchant-pay-summary {
  display: inline-block;
  max-width: 100%;
  margin-top: 10px;
  text-align: left;
}

.merchant-pay-summary-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 12px;
  font-weight: 600;
}

.merchant-pay-summary-title span {
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
}

.merchant-pay-summary-scroll {
  max-width: 100%;
  overflow-x: auto;
}

.merchant-pay-summary-table {
  border-collapse: collapse;
  color: rgba(0, 0, 0, 0.75);
  font-size: 12px;
  white-space: nowrap;
}

.merchant-pay-summary-table th,
.merchant-pay-summary-table td {
  padding: 5px 10px;
  border: 1px solid #f0f0f0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.merchant-pay-summary-table th {
  background: #fafafa;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
}

.merchant-pay-summary-table .merchant-pay-summary-label {
  text-align: left;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 600;
}

.merchant-pay-summary-table .merchant-pay-summary-total {
  border-left-color: #d9d9d9;
  background: #fafafa;
  font-weight: 600;
}

.merchant-pay-summary-cell {
  min-width: 210px;
}

.merchant-pay-summary-amount {
  color: rgba(0, 0, 0, 0.75);
  font-weight: 600;
  line-height: 18px;
}

.merchant-pay-summary-counts {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
}

.merchant-pay-summary-counts span {
  white-space: nowrap;
}

.merchant-pay-summary-separator {
  color: rgba(0, 0, 0, 0.45);
}

.merchant-list-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

.merchant-list-label {
  font-weight: 600;
}

.merchant-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 220px;
  padding: 2px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fafafa;
  color: rgba(0, 0, 0, 0.75);
  font: inherit;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: opacity 0.2s, border-color 0.2s, background 0.2s;
}

.merchant-tag.is-off {
  opacity: 0.35;
  text-decoration: line-through;
}

.merchant-tag-line {
  flex: 0 0 auto;
  width: 18px;
  height: 0;
  border-top: 2px solid #666;
}

.merchant-tag-dot {
  flex: 0 0 auto;
  width: 1em;
  height: 1em;
  border-radius: 50%;
}

.merchant-list-empty {
  color: rgba(0, 0, 0, 0.45);
}

.ch-line {
  display: inline-block;
  width: 28px;
  height: 0;
  border-top: 2.5px solid #666;
}
.ch-solid {
  border-top-style: solid;
}
.ch-dash {
  border-top-style: dashed;
}
.ch-dot {
  border-top-style: dotted;
}
.ch-dashdot {
  border-top: 2px dashed #666;
  background: repeating-linear-gradient(
    90deg,
    #666 0 6px,
    transparent 6px 10px,
    #666 10px 12px,
    transparent 12px 16px
  );
  height: 2px;
  border: none;
}

.deduct-stat-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  /* padding: 0px 0 0; */
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.deduct-stat-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}

.deduct-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.stat-card {
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.stat-card :deep(.ant-card-body) {
  flex: 1;
  min-width: 0;
}

.bean-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  min-width: 0;
}

.bean-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
}

.task-record-card :deep(.ant-spin-container) {
  min-width: 0;
}

.task-record-summary {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 8px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.75);
  font-size: 12px;
}

.task-record-summary-item {
  white-space: nowrap;
}

.task-record-summary-value {
  color: #1677ff;
  font-size: 18px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.task-record-amount-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.task-record-trend-chart {
  width: 100%;
  height: 120px;
}

.amount-group-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.st-th {
  padding: 6px 12px;
  text-align: center;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

.st-label-col {
  text-align: left;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  width: 80px;
}

.st-td {
  padding: 5px 10px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}

.st-row:hover td {
  background: #fafafa;
}

.st-row-alt td {
  background: #fafafa;
}

.st-row-alt:hover td {
  background: #f0f0f0;
}

/* ===== 留存率热力表 ===== */
.retention-card :deep(.ant-card-head) {
  position: relative;
}

.retention-center-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 250;
  color: #333;
  pointer-events: none;
}

.retention-card :deep(.ant-card-body) {
  padding: 0;
}

.retention-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
}

.retention-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  white-space: nowrap;
}

.rt-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fb;
  padding: 8px 14px;
  text-align: center;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eceef2;
}

.rt-th.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 3;
  text-align: center;
}

.rt-td {
  padding: 4px 14px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.rt-rate {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.rt-sub {
  font-size: 11px;
  opacity: 0.55;
  margin-top: 2px;
  font-weight: 400;
}

.rt-td.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  font-weight: 500;
  text-align: center;
  color: #333;
}

.retention-table tbody tr:hover td {
  background: rgba(24, 144, 255, 0.04);
}

.retention-table tbody tr:hover td.rt-fixed {
  background: #f0f7ff;
}

/* 整行渠道底色：使用实色 pastel，不依赖 hover 即可看出区别；
   热力单元格的 inline style 会自动覆盖，不影响留存率颜色 */
.bank-retention-table tbody tr.bk-row-kq > td {
  background-color: #e6f0ff !important;
}

.bank-retention-table tbody tr.bk-row-hlb > td {
  background-color: #e8f7e1 !important;
}

.bank-retention-table tbody tr.bk-row-jd > td {
  background-color: #fff2d9 !important;
}

.bank-retention-table tbody tr.bk-row-default > td {
  background-color: #f0f0f0 !important;
}

/* meta 列（渠道、M0）加深一档便于识别 */
.bank-retention-table tbody tr.bk-row-kq .bk-meta-cell {
  background-color: #bfdbff !important;
}

.bank-retention-table tbody tr.bk-row-hlb .bk-meta-cell {
  background-color: #c8eab3 !important;
}

.bank-retention-table tbody tr.bk-row-jd .bk-meta-cell {
  background-color: #ffe0a3 !important;
}

.bank-retention-table tbody tr.bk-row-default .bk-meta-cell {
  background-color: #d9d9d9 !important;
}

/* 日期合并单元格保持中性，避免和渠道色抢视觉 */
.bank-retention-table tbody tr .bk-date-cell {
  background-color: #fff !important;
}

.bank-retention-table tbody tr .bk-channel-cell {
  font-weight: 600;
  box-shadow: inset 4px 0 0 transparent;
}

.bank-retention-table tbody tr.bk-row-kq .bk-channel-cell {
  color: #0958d9;
  box-shadow: inset 4px 0 0 #1677ff;
}

.bank-retention-table tbody tr.bk-row-hlb .bk-channel-cell {
  color: #389e0d;
  box-shadow: inset 4px 0 0 #52c41a;
}

.bank-retention-table tbody tr.bk-row-jd .bk-channel-cell {
  color: #d48806;
  box-shadow: inset 4px 0 0 #faad14;
}

.bank-retention-table tbody tr.bk-row-default .bk-channel-cell {
  color: rgba(0, 0, 0, 0.7);
  box-shadow: inset 4px 0 0 rgba(0, 0, 0, 0.2);
}

.bank-retention-table tbody tr.bk-row-kq:hover .bk-meta-cell {
  background-color: #a3c8ff !important;
}

.bank-retention-table tbody tr.bk-row-hlb:hover .bk-meta-cell {
  background-color: #aedb95 !important;
}

.bank-retention-table tbody tr.bk-row-jd:hover .bk-meta-cell {
  background-color: #ffd180 !important;
}

.bank-retention-table tbody tr.bk-row-default:hover .bk-meta-cell {
  background-color: #bfbfbf !important;
}
</style>

<style>
/* 留存分析指标说明弹层（非 scoped，popover 渲染在 body） */
.retention-help-popover .ant-popover-inner {
  max-width: 420px;
}
.rh-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
  line-height: 1.7;
  color: #444;
}
.rh-label {
  font-weight: 600;
  margin-right: 4px;
}
</style>
